<template>
  <div class="resource-row">
    <div class="resource-fields">
      <el-input v-model="resource.title" placeholder="Resource title" aria-label="Resource title" />
      <el-select v-model="resource.audience" aria-label="Resource audience">
        <el-option label="Public" value="public" />
        <el-option label="Educators" value="educator" />
        <el-option label="Students" value="student" />
      </el-select>
      <el-button :disabled="uploadsActive" @click="$emit('remove')">Remove</el-button>
    </div>
    <div class="resource-file">
      <el-input v-model="resource.url" :disabled="uploading" placeholder="Paste a resource link or upload a file" aria-label="Resource URL" />
      <input ref="fileInput" type="file" class="file-input" tabindex="-1" aria-label="Choose resource file" @change="chooseFile">
      <el-button :loading="uploading" :disabled="uploading" icon="el-icon-upload2" @click="$refs.fileInput.click()">
        {{ uploading ? 'Uploading…' : resource.url ? 'Replace with file' : 'Upload file' }}
      </el-button>
    </div>
    <div v-if="uploading" class="upload-status" role="status">
      <span>{{ fileName }}</span>
      <el-progress :percentage="progress" :stroke-width="5" />
    </div>
    <p v-if="uploadError" class="upload-error" role="alert">{{ uploadError }}</p>
    <p v-else-if="uploadedName" class="upload-success" role="status">{{ uploadedName }} uploaded. Save the course to keep this resource.</p>
  </div>
</template>

<script>
import * as qiniu from 'qiniu-js'
import { postRequest } from '@/apis/lfAdmin'

const MAX_FILE_BYTES = 100 * 1024 * 1024
export default {
  name: 'EducatorResourceRow',
  props: {
    resource: { type: Object, required: true },
    uploadsActive: { type: Boolean, default: false }
  },
  data() {
    return { uploading: false, progress: 0, fileName: '', uploadError: '', uploadedName: '' }
  },
  created() { this.uploadSubscription = null; this.disposed = false },
  beforeDestroy() {
    this.disposed = true
    if (this.uploadSubscription) this.uploadSubscription.unsubscribe()
    this.finishUpload()
  },
  methods: {
    finishUpload() {
      this.uploadSubscription = null
      if (this.uploading) {
        this.uploading = false
        this.$emit('uploading-change', false)
      }
    },
    async chooseFile(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file || this.uploading) return
      this.uploadError = ''
      this.uploadedName = ''
      if (!file.size || file.size > MAX_FILE_BYTES) {
        this.uploadError = 'Choose a non-empty file up to 100 MB.'
        return
      }
      this.uploading = true
      this.progress = 0
      this.fileName = file.name
      this.$emit('uploading-change', true)
      try {
        const response = await postRequest('getSingaporeQiniuToken')
        if (this.disposed) return
        if (Number(response.data.code) !== 0 || typeof response.data.data !== 'string' || !response.data.data) {
          throw new Error(response.data.msg || 'Unable to prepare the upload. Please retry.')
        }
        const filename = file.name.replace(/[^a-zA-Z0-9._-]/g, '_').slice(-160) || 'resource'
        const key = `educator-resources/${crypto.randomUUID()}/${filename}`
        this.uploadSubscription = qiniu.upload(file, key, response.data.data, { fname: file.name }, { useCdnDomain: true }).subscribe({
          next: result => {
            if (!this.disposed) this.progress = Math.min(99, Math.max(0, Math.round(result.total.percent)))
          },
          error: () => {
            if (!this.disposed) this.uploadError = 'Upload failed. Your previous link is unchanged. Choose the file again to retry.'
            this.finishUpload()
          },
          complete: result => {
            if (!this.disposed) {
              this.resource.url = `https://ap.knowit.today/${encodeURIComponent(result.key || key).replace(/%2F/g, '/')}`
              if (!this.resource.title.trim()) this.resource.title = file.name
              this.progress = 100
              this.uploadedName = file.name
            }
            this.finishUpload()
          }
        })
      } catch (error) {
        if (!this.disposed) this.uploadError = error.message || 'Upload failed. Please retry.'
        this.finishUpload()
      }
    }
  }
}
</script>

<style scoped>
.resource-row { margin: 12px 0; padding: 14px; border: 1px solid #e4e7ed; border-radius: 6px; background: #fafbfc; }
.resource-fields { display: grid; grid-template-columns: minmax(0, 1fr) 140px auto; gap: 8px; }
.resource-file { display: flex; gap: 8px; margin-top: 8px; }
.file-input { display: none; }
.upload-status { margin-top: 10px; color: #606266; font-size: 12px; overflow-wrap: anywhere; }
.upload-status .el-progress { margin-top: 6px; }
.upload-error, .upload-success { margin: 8px 0 0; font-size: 12px; overflow-wrap: anywhere; }
.upload-error { color: #b42318; }
.upload-success { color: #277455; }
@media (max-width: 600px) {
  .resource-fields { grid-template-columns: minmax(0, 1fr) auto; }
  .resource-fields > .el-input { grid-column: 1 / -1; }
  .resource-file { flex-wrap: wrap; }
}
</style>
